import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Instagram, Linkedin } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { site } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  useReveal();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet. Wir melden uns schnellstmöglich.");
      setForm(initial);
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte rufen Sie uns an oder schreiben per WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Telefon", value: site.phoneDisplay, href: `tel:${site.phoneTel}` },
    { icon: MessageCircle, label: "WhatsApp", value: site.phoneDisplay, href: site.whatsapp },
    { icon: Mail, label: "E-Mail", value: site.email, href: `mailto:${site.email}` },
    { icon: MapPin, label: "Adresse", value: `${site.street}, ${site.city}`, href: site.maps },
    { icon: Clock, label: "Öffnungszeiten", value: site.hours },
  ];

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl reveal mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Kontakt</p>
          <h2 className="font-heading font-bold text-white tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight">
            Anfrage stellen – wir melden uns.
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed">
            Schreiben Sie uns oder rufen Sie direkt an. Am schnellsten geht es
            per Telefon oder WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="reveal rounded-2xl border border-white/10 bg-card p-8 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *" testid="contact-name">
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-input-name"
                  className="input"
                  placeholder="Ihr Name"
                />
              </Field>
              <Field label="E-Mail *" testid="contact-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  className="input"
                  placeholder="name@email.de"
                />
              </Field>
              <Field label="Telefon" testid="contact-phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-input-phone"
                  className="input"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Betreff" testid="contact-subject">
                <input
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  data-testid="contact-input-subject"
                  className="input"
                  placeholder="z. B. Wochenendmiete"
                />
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Nachricht *" testid="contact-message">
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="input resize-none"
                  placeholder="Wann und wofür möchten Sie den Tesla nutzen?"
                />
              </Field>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-8 py-4 text-base font-medium hover:bg-white/85 transition-colors disabled:opacity-60"
            >
              {loading ? "Wird gesendet…" : "Anfrage senden"}
              {!loading && <Send className="w-4 h-4" />}
            </button>

            <style>{`.input{width:100%;background:hsl(var(--input)/0.4);border:1px solid rgba(255,255,255,0.12);border-radius:12px;padding:0.75rem 1rem;color:#fff;font-size:0.9rem;outline:none;transition:border-color .2s}.input::placeholder{color:rgba(255,255,255,0.35)}.input:focus{border-color:rgba(255,255,255,0.5)}`}</style>
          </form>

          {/* Info + map */}
          <div className="reveal flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-card p-8 md:p-10">
              <div className="space-y-5">
                {info.map((it) => {
                  const content = (
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center shrink-0">
                        <it.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40">{it.label}</p>
                        <p className="text-white text-sm mt-1">{it.value}</p>
                      </div>
                    </div>
                  );
                  return it.href ? (
                    <a
                      key={it.label}
                      href={it.href}
                      target={it.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={`contact-info-${it.label.toLowerCase()}`}
                      className="block hover:opacity-80 transition-opacity"
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={it.label} data-testid={`contact-info-${it.label.toLowerCase()}`}>{content}</div>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-8">
                <a
                  href={site.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-5 py-3 text-sm font-medium hover:bg-white/85 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <a
                  href={site.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-5 py-3 text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
                <a
                  href={site.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-5 py-3 text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/10 flex-1 min-h-[280px]">
              <iframe
                title="Standort Tesla-Verleih-Frankfurt"
                src={site.mapsEmbed}
                data-testid="contact-map"
                className="w-full h-full min-h-[280px]"
                style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-white/50 mb-2 block">{label}</span>
    {children}
  </label>
);
